import React, { useEffect, useRef } from "react"
import { useStickyTableHeaders } from "@/lib/useStickyTableHeaders"

interface HtmlSectionBodyProps {
  html: string | null
}

export function HtmlSectionBody({ html }: HtmlSectionBodyProps) {
  const ref = useRef<HTMLDivElement>(null)
  useStickyTableHeaders(ref, [html])

  useEffect(() => {
    if (!ref.current) return
    // Open all links in new tab with explicit click handling
    const linkClickHandler = (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      let href = (e.currentTarget as HTMLAnchorElement).getAttribute("href")
      if (href) {
        // Ensure protocol so bare domains don't resolve as relative paths
        if (!/^(https?:\/\/|mailto:|tel:)/i.test(href)) {
          href = "https://" + href
        }
        window.open(href, "_blank", "noopener,noreferrer")
      }
    }
    ref.current.querySelectorAll("a").forEach((link) => {
      link.setAttribute("target", "_blank")
      link.setAttribute("rel", "noopener noreferrer")
      link.addEventListener("click", linkClickHandler)
    })
    const currentRef = ref.current
    // Replace native <details> with JS-driven collapsible divs
    ref.current.querySelectorAll<HTMLDetailsElement>("details").forEach((details) => {
      const wrapper = document.createElement("div")
      wrapper.className = "js-collapsible"

      const summaryEl = details.querySelector("summary")
      const trigger = document.createElement("div")
      trigger.className = "js-collapsible-trigger"
      trigger.textContent = summaryEl ? summaryEl.textContent : "Details"
      wrapper.appendChild(trigger)

      const body = document.createElement("div")
      body.className = "js-collapsible-body"
      body.style.display = "none"
      // Move all children except summary into body
      const children = Array.from(details.childNodes)
      for (const child of children) {
        if (child !== summaryEl) {
          body.appendChild(child)
        }
      }
      wrapper.appendChild(body)

      trigger.addEventListener("click", () => {
        const isOpen = wrapper.classList.toggle("is-open")
        body.style.display = isOpen ? "" : "none"
        // Trigger sticky header recalculation for tables inside collapsibles
        if (isOpen) {
          requestAnimationFrame(() => window.dispatchEvent(new Event("resize")))
        }
      })

      details.replaceWith(wrapper)
    })
    // Citation hover titles
    ref.current
      .querySelectorAll<HTMLSpanElement>(".compliance-citation")
      .forEach((span) => {
        const codes = span.getAttribute("data-codes") || span.textContent || ""
        span.setAttribute("title", codes)
      })
    return () => {
      currentRef?.querySelectorAll("a").forEach((link) => {
        link.removeEventListener("click", linkClickHandler)
      })
    }
  }, [html])

  if (!html) return null

  return (
    <div
      ref={ref}
      className="prose max-w-none dark:prose-invert prose-td:border prose-td:px-3 prose-td:py-1.5 prose-th:border prose-th:px-3 prose-th:py-1.5 prose-th:bg-accent prose-th:text-accent-foreground prose-a:text-[#3693D0] prose-a:underline prose-hr:border-[#FF907D]"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}
